import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { UnidataTable } from '~/components';
import {
  DESCARGA_NF,
  MODULO_20_GRAUS,
  SOBRAS_E_FALTAS,
  SKID,
  MODULO_CUSTOMIZACAO_RUMO,
  MODULO_FERROVIA,
  MODULOFOTO,
  LOCALIDADE_ABASTECIMENTO,
} from '~/constants/modules';
import { t, can } from '~/helpers';
import { handleExportGeneric } from '~/helpers/exportReports';
import {
  deletePosto,
  findPosto,
  recordFiltering,
} from '~/store/modules/posto/actions';

import Form from './form';

function PostoList() {
  const redux = useSelector((state) => state.posto);

  const status = useMemo(
    () => [
      { label: 'L', value: t('s.geral.opcoes.liberado') },
      { label: 'B', value: t('s.geral.opcoes.bloqueado') },
      { label: 'C', value: t('s.geral.opcoes.cancelado') },
    ],
    []
  );

  const statusSimNao = useMemo(
    () => [
      { label: 0, value: t('s.geral.opcoes.nao') },
      { label: 1, value: t('s.geral.opcoes.sim') },
    ],
    []
  );

  const header = useMemo(() => {
    const data = [
      {
        id: 'codigoposto',
        label: t('s.configuracao.posto.idposto'),
        filter: { type: 'text' },
      },
      {
        id: 'nomeposto',
        label: t('s.configuracao.posto.nomeposto'),
        filter: { type: 'text' },
      },
      {
        id: 'codigoauxiliarposto',
        label: t('s.configuracao.posto.codigoauxiliarposto'),
        filter: { type: 'text' },
      },
      {
        id: 'nomeabreviadoposto',
        label: t('s.configuracao.posto.nomeabreviadoposto'),
        filter: { type: 'text' },
      },
      {
        id: 'statusposto',
        label: t('s.configuracao.posto.statusposto'),
        formatted: (value) =>
          status.find((s) => s.label === value)?.value || '',
        filter: { type: 'select', options: status },
      },
    ];

    if (can(DESCARGA_NF) && can(MODULO_20_GRAUS)) {
      data.splice(4, 0, {
        id: 'opercompartimentosposto',
        label: t('s.configuracao.posto.opercompartimentos'),
        formatted: (value) =>
          statusSimNao.find((s) => s.label === value)?.value || '',
      });
    }

    if (can(MODULO_20_GRAUS)) {
      data.splice(5, 0, {
        id: 'controla20grausposto',
        label: t('s.configuracao.posto.controla20graus'),
        formatted: (value) =>
          statusSimNao.find((s) => s.label === value)?.value || '',
      });
    }

    if (can(DESCARGA_NF) && can(MODULO_20_GRAUS)) {
      data.splice(6, 0, {
        id: 'operrecebimento20posto',
        label: t('s.configuracao.posto.operrecebimento20'),
        formatted: (value) =>
          statusSimNao.find((s) => s.label === value)?.value || '',
      });
    }

    if (can(MODULO_20_GRAUS)) {
      data.splice(6, 0, {
        id: 'operabastecimento20posto',
        label: t('s.configuracao.posto.operabastecimento20'),
        formatted: (value) =>
          statusSimNao.find((s) => s.label === value)?.value || '',
      });
    }

    if (can(LOCALIDADE_ABASTECIMENTO)) {
      data.splice(4, 0, {
        id: 'nomelocalidadeposto',
        label: t('s.configuracao.posto.localidade'),
        formatted: (value) => value || '',
        filter: { type: 'text' },
      });
    }
    return data;
  }, [status]);

  // impressao pdf ou xls

  const fieldlist = [
    'codigoposto',
    'nomeposto',
    'codigoauxiliarposto',
    'nomeabreviadoposto',
    'ajustehoraposto',
    'depositoposto',
    'centroposto',
    'cnpjposto',
  ];

  if (can(DESCARGA_NF) && can(SKID)) {
    fieldlist.push('habilitaskidposto');
  }

  if (can(DESCARGA_NF) && can(MODULO_20_GRAUS)) {
    fieldlist.push(
      'operrecebimento20posto',
      'opercompartimentosposto',
      'operamostracompartimentossemreceb20posto'
    );
  }

  if (can(DESCARGA_NF) && can(MODULO_CUSTOMIZACAO_RUMO)) {
    fieldlist.push('habilitainspesaodescargaposto');
  }

  if (can(MODULO_20_GRAUS)) {
    fieldlist.push('controla20grausposto', 'operabastecimento20posto');
  }

  if (can(LOCALIDADE_ABASTECIMENTO)) {
    fieldlist.push('nomelocalidadeposto');
  }

  if (can(SOBRAS_E_FALTAS)) {
    fieldlist.push('transfereturnoposto');
    fieldlist.push('enderecoposto');
    fieldlist.push('municipioposto');
    fieldlist.push('ufposto');
    fieldlist.push('inscricaoestadualposto');
    fieldlist.push('inscricaomunicipalposto');
    fieldlist.push('nomedistribuidoraposto');
  }

  if (can(MODULO_FERROVIA)) {
    fieldlist.push('controlapitstopposto');
  }

  if (can(DESCARGA_NF)) {
    fieldlist.push('tipodescargaposto');
  }

  if (can(DESCARGA_NF) && can(MODULOFOTO)) {
    fieldlist.push('habilitafotonotafiscalposto');
  }

  fieldlist.push('utilinfoabastposto');
  fieldlist.push('solicfrentsegundlibposto');
  fieldlist.push('statusposto');

  const substituirHeader = {
    codigoposto: t('s.configuracao.posto.idposto'),
    nomeposto: t('s.configuracao.posto.nomeposto'),
    codigoauxiliarposto: t('s.configuracao.posto.codigoauxiliarposto'),
    nomeabreviadoposto: t('s.configuracao.posto.nomeabreviadoposto'),
    statusposto: t('s.configuracao.posto.statusposto'),
    cnpjposto: t('s.configuracao.posto.cnpjposto'),
    ajustehoraposto: t('s.configuracao.posto.ajustehoraposto'),
    depositoposto: t('s.geral.camposistema.depositoposto'),
    centroposto: t('s.geral.camposistema.centroposto'),
    habilitaskidposto: t('s.configuracao.posto.habilitaskidposto'),
    opercompartimentosposto: t('s.configuracao.posto.opercompartimentos'),
    operrecebimento20posto: t('s.configuracao.posto.operrecebimento20'),
    operamostracompartimentossemreceb20posto: t(
      's.configuracao.posto.operamostracompartimentossemreceb20posto'
    ),
    habilitainspesaodescargaposto: t(
      's.configuracao.posto.habilitainspesaodescargaposto'
    ),
    controla20grausposto: t('s.configuracao.posto.controla20graus'),
    operabastecimento20posto: t('s.configuracao.posto.operabastecimento20'),
    transfereturnoposto: t('s.configuracao.posto.transfereturnoposto'),
    enderecoposto: t('s.configuracao.posto.enderecoposto'),
    municipioposto: t('s.configuracao.posto.municipioposto'),
    ufposto: t('s.configuracao.posto.ufposto'),
    inscricaoestadualposto: t('s.configuracao.posto.inscricaoestadualposto'),
    inscricaomunicipalposto: t('s.configuracao.posto.inscricaomunicipalposto'),
    nomedistribuidoraposto: t('s.configuracao.posto.nomedistribuidoraposto'),
    controlapitstopposto: t('s.configuracao.posto.controlapitstopposto'),
    tipodescargaposto: t('s.configuracao.posto.tipodescargaposto'),
    habilitafotonotafiscalposto: t(
      's.configuracao.posto.habilitafotonotafiscalposto'
    ),
    nomelocalidadeposto: t('s.configuracao.posto.localidade'),
  };

  return (
    <UnidataTable
      fixed
      redux={redux}
      header={header}
      read={recordFiltering}
      find={findPosto}
      remove={deletePosto}
      form={Form}
      keys="idposto"
      detach="nomeposto"
      pdf
      excel
      onExport={(formato) =>
        handleExportGeneric({
          endpoint: 'postos',
          formato,
          fields: fieldlist,
          headers: substituirHeader,
          redux,
          ordination: redux.params.ordination,
          orderfield: redux.params.orderfield,
          title: 'Relatório de Postos',
          substituirValores: {
            statusposto: {
              L: t('s.geral.opcoes.liberado'),
              B: t('s.geral.opcoes.bloqueado'),
              C: t('s.geral.opcoes.cancelado'),
            },
            habilitaskidposto: {
              N: t('s.geral.opcoes.nao'),
              S: t('s.geral.opcoes.sim'),
            },
            tipodescargaposto: {
              '1': t('s.geral.opcoes.descarganftanque'),
              '2': t('s.geral.opcoes.descarganf'),
              '0': 'N/A',
            },
            transfereturnoposto: {
              '1': t('s.geral.opcoes.sim'),
              '0': t('s.geral.opcoes.nao'),
            },
            habilitafotonotafiscalposto: {
              N: t('s.geral.opcoes.nao'),
              S: t('s.geral.opcoes.sim'),
            },
            opercompartimentosposto: {
              '1': t('s.geral.opcoes.sim'),
              '0': t('s.geral.opcoes.nao'),
            },
            operrecebimento20posto: {
              '1': t('s.geral.opcoes.sim'),
              '0': t('s.geral.opcoes.nao'),
            },
            controla20grausposto: {
              '1': t('s.geral.opcoes.sim'),
              '0': t('s.geral.opcoes.nao'),
            },
            operabastecimento20posto: {
              '1': t('s.geral.opcoes.sim'),
              '0': t('s.geral.opcoes.nao'),
            },
            habilitainspesaodescargaposto: {
              N: t('s.geral.opcoes.nao'),
              S: t('s.geral.opcoes.sim'),
            },
            operamostracompartimentossemreceb20posto: {
              '1': t('s.geral.opcoes.sim'),
              '0': t('s.geral.opcoes.nao'),
            },
            controlapitstopposto: {
              N: t('s.geral.opcoes.nao'),
              S: t('s.geral.opcoes.sim'),
            },
          },
        })
      }
    />
  );
}

export default PostoList;
